<template>
  <v-navigation-drawer
    v-bind:value="drawer"
    @input="$emit('update:drawer', $event)"
    app
    temporary
    dark
    class="industrial-nav"
    style="z-index: 9999;"
  >
    <div class="nav-header pa-4">
      <v-img
        v-if="logoSrc"
        contain
        max-height="48"
        max-width="160"
        :src="logoSrc"
        class="nav-header__logo"
        @error="onLogoError"
      />
      <div class="nav-header__name">{{ appName }}</div>
      <div class="nav-header__tagline">{{ appTagline }}</div>
    </div>

    <v-divider class="nav-divider" />

    <v-list-item v-if="tenantList.length > 1" class="px-3">
      <v-select
        class="mt-2 nav-tenant-select"
        dense
        outlined
        dark
        label="เลือกลูกค้า"
        :items="tenantList"
        item-text="TenantName"
        return-object
        v-model="tenant"
        hide-details
      />
    </v-list-item>

    <v-divider v-if="tenantList.length > 1" class="nav-divider" />

    <v-list nav dense class="nav-list">
      <template v-for="(group, gIndex) in menuGroups">
        <v-subheader :key="'header-' + gIndex" class="nav-group-header">
          {{ group.label }}
        </v-subheader>
        <v-list-item
          v-for="(item, index) in group.items"
          :key="gIndex + '-' + index"
          :to="item.navigate === '/' ? '/' : { name: item.navigate }"
          class="nav-item"
          active-class="nav-item--active"
        >
          <v-list-item-icon>
            <v-icon>{{ item.icons }}</v-icon>
          </v-list-item-icon>
          <v-list-item-title>{{ item.service }}</v-list-item-title>
        </v-list-item>
      </template>
    </v-list>

    <template v-slot:append>
      <div class="nav-footer pa-3">
        <small>© {{ currentYear }} {{ copyrightHolder }} v{{ version }}</small>
      </div>
    </template>
  </v-navigation-drawer>
</template>

<script>
import { mapState } from 'vuex'
import { resolveBranding } from '@/utils/tenantBranding'
import { APP_NAME, APP_TAGLINE_TH, COPYRIGHT_HOLDER } from '@/config/appBrand'

const ALL_MENUS = [
  { name: 'All', service: 'Operations Overview', icons: 'mdi-view-dashboard-outline', navigate: '/', group: 'operations' },
  { name: 'Alert', service: 'Alert', icons: 'mdi-alert-outline', navigate: 'alert', group: 'operations' },
  { name: 'EnergyAlerts', service: 'Energy Alerts', icons: 'mdi-flash-alert', navigate: 'energy_alert_settings', group: 'operations' },
  { name: 'LineQuota', service: 'LINE Quota', icons: 'mdi-chat-processing-outline', navigate: 'line_quota', group: 'operations' },
  { name: 'Asset', service: 'Asset', icons: 'mdi-toy-brick-outline', navigate: 'asset', group: 'assets' },
  { name: 'Network', service: 'Network', icons: 'mdi-network-outline', navigate: 'network', group: 'assets' },
  { name: 'Report', service: 'Report', icons: 'mdi-chart-box-outline', navigate: 'report', group: 'reports' },
  { name: 'Users', service: 'Users', icons: 'mdi-account-multiple-check-outline', navigate: 'users', group: 'system' },
  { name: 'Admin', service: 'Admin', icons: 'mdi-cog', navigate: 'admin', group: 'system', superAdminOnly: true },
]

const GROUP_LABELS = {
  operations: 'ปฏิบัติการ',
  assets: 'ทรัพย์สิน',
  reports: 'รายงาน',
  system: 'ระบบ',
}

export default {
  props: {
    drawer: {
      type: Boolean
    },
  },
  data() {
    return {
      logoError: false,
      version: process.env.VUE_APP_VERSION,
      appName: APP_NAME,
      appTagline: APP_TAGLINE_TH,
      copyrightHolder: COPYRIGHT_HOLDER,
      currentYear: new Date().getFullYear(),
    }
  },
  watch: {
    tenantBranding() {
      this.logoError = false
    }
  },
  computed: {
    ...mapState('User', ['tenantList', 'tenantBranding']),
    isSuperAdmin() {
      return this.$store.state.User?.isSupperAdmin === true
    },
    logoSrc() {
      if (this.logoError) return null
      return resolveBranding(this.tenantBranding).logo
    },
    menuGroups() {
      const visible = ALL_MENUS.filter(
        item => !item.superAdminOnly || this.isSuperAdmin
      )
      const groups = ['operations', 'assets', 'reports', 'system']
      return groups.map(key => ({
        label: GROUP_LABELS[key],
        items: visible.filter(item => item.group === key),
      })).filter(g => g.items.length > 0)
    },
    tenant: {
      get() {
        return this.$store.state.User.tenant
      },
      set(newValue) {
        this.$store.dispatch('User/SET_TENANT', newValue).catch((err) => {
          console.log(err)
        })
      }
    },
  },
  methods: {
    onLogoError() {
      this.logoError = true
    },
  },
}
</script>

<style scoped>
.industrial-nav {
  background: var(--chrome-dark, #1C1F26) !important;
}

.nav-header {
  text-align: center;
}

.nav-header__logo {
  margin: 0 auto;
}

.nav-header__name {
  margin-top: 8px;
  font-size: 1rem;
  font-weight: 600;
  color: #E5E7EB;
  letter-spacing: 0.04em;
}

.nav-header__tagline {
  font-size: 0.72rem;
  color: rgba(229, 231, 235, 0.55);
  margin-top: 2px;
}

.nav-divider {
  border-color: var(--chrome-border, #2D3340) !important;
}

.nav-group-header {
  color: var(--accent-amber, #D97706) !important;
  font-size: 0.65rem !important;
  font-weight: 600 !important;
  letter-spacing: 0.1em !important;
  text-transform: uppercase;
  height: 32px !important;
}

.nav-list >>> .v-list-item {
  color: #E5E7EB !important;
}

.nav-list >>> .v-list-item__title {
  color: #E5E7EB !important;
}

.nav-list >>> .v-list-item__icon .v-icon {
  color: rgba(229, 231, 235, 0.7) !important;
}

.nav-item--active {
  background: rgba(217, 119, 6, 0.12) !important;
  border-left: 3px solid var(--accent-amber, #D97706);
}

.nav-item--active >>> .v-list-item__icon .v-icon {
  color: var(--accent-amber, #D97706) !important;
}

.nav-item--active >>> .v-list-item__title {
  color: #fff !important;
  font-weight: 600;
}

.nav-tenant-select >>> .v-input__slot {
  border-color: var(--chrome-border, #2D3340) !important;
}

.nav-footer {
  border-top: 1px solid var(--chrome-border, #2D3340);
  color: rgba(229, 231, 235, 0.4);
  text-align: center;
}
</style>
